import React, { useState } from 'react';
import { 
  Brain, 
  Shield, 
  Zap, 
  Eye, 
  Heart, 
  Smartphone, 
  Globe, 
  Lock,
  TrendingUp,
  Users,
  Database,
  Cpu
} from 'lucide-react';
import { theme } from '../../styles/theme';

interface InnovativeFeature {
  id: string;
  title: string;
  description: string;
  details: string;
  icon: React.ComponentType<any>;
  status: 'in-development' | 'beta' | 'released' | 'planned';
  priority: 'low' | 'medium' | 'high';
  category: 'ai' | 'security' | 'health' | 'mobile' | 'analytics';
}

export const InnovativeFeatures: React.FC = () => {
  const [activeFeature, setActiveFeature] = useState<string | null>(null);

  const innovativeFeatures: InnovativeFeature[] = [
    {
      id: 'ai-health-assistant',
      title: 'AI Health Assistant',
      description: 'Personalized health recommendations powered by advanced AI',
      details: 'Our AI assistant analyzes your health data, lifestyle patterns, and medical history to provide personalized recommendations. It learns from your interactions and adapts to your specific health goals and conditions.',
      icon: Brain,
      status: 'beta',
      priority: 'high',
      category: 'ai'
    },
    {
      id: 'blockchain-security',
      title: 'Blockchain Health Records',
      description: 'Secure, decentralized health data storage',
      details: 'Leveraging blockchain technology for immutable, secure health records. Your data is encrypted and distributed across a secure network, ensuring privacy and preventing unauthorized access.',
      icon: Shield,
      status: 'in-development',
      priority: 'high',
      category: 'security'
    },
    {
      id: 'real-time-monitoring',
      title: 'Real-time Health Monitoring',
      description: 'Continuous health tracking with instant alerts',
      details: 'Advanced sensors and IoT devices provide real-time health monitoring. Get instant alerts for critical changes in vital signs, medication reminders, and emergency notifications.',
      icon: Zap,
      status: 'released',
      priority: 'medium',
      category: 'health'
    },
    {
      id: 'predictive-analytics',
      title: 'Predictive Health Analytics',
      description: 'AI-powered health predictions and risk assessment',
      details: 'Machine learning algorithms analyze patterns in your health data to predict potential health issues before they become serious. Early warning system for preventive care.',
      icon: TrendingUp,
      status: 'beta',
      priority: 'high',
      category: 'analytics'
    },
    {
      id: 'mobile-health-app',
      title: 'Mobile Health Companion',
      description: 'Comprehensive health tracking on your smartphone',
      details: 'Full-featured mobile app with offline capabilities, health tracking, medication reminders, and seamless integration with wearable devices and health sensors.',
      icon: Smartphone,
      status: 'released',
      priority: 'medium',
      category: 'mobile'
    },
    {
      id: 'global-health-network',
      title: 'Global Health Network',
      description: 'Connect with healthcare providers worldwide',
      details: 'Access to a global network of healthcare professionals, specialists, and medical facilities. Telemedicine consultations, second opinions, and international health coordination.',
      icon: Globe,
      status: 'planned',
      priority: 'low',
      category: 'health'
    },
    {
      id: 'privacy-protection',
      title: 'Advanced Privacy Protection',
      description: 'Zero-knowledge encryption and privacy controls',
      details: 'State-of-the-art encryption ensures your health data remains private. Zero-knowledge architecture means even we cannot access your personal health information.',
      icon: Lock,
      status: 'released',
      priority: 'high',
      category: 'security'
    },
    {
      id: 'community-health',
      title: 'Health Community Platform',
      description: 'Connect with others on similar health journeys',
      details: 'Join communities of people with similar health conditions, goals, or interests. Share experiences, get support, and learn from others while maintaining privacy.',
      icon: Users,
      status: 'beta',
      priority: 'medium',
      category: 'health'
    },
    {
      id: 'health-data-warehouse',
      title: 'Health Data Warehouse',
      description: 'Comprehensive health data management system',
      details: 'Centralized platform for storing, analyzing, and sharing health data across multiple sources. Integrates with EHR systems, wearable devices, and health apps.',
      icon: Database,
      status: 'in-development',
      priority: 'high',
      category: 'analytics'
    },
    {
      id: 'ai-diagnostic-tools',
      title: 'AI Diagnostic Tools',
      description: 'Advanced diagnostic assistance for healthcare providers',
      details: 'AI-powered tools that assist healthcare providers in diagnosis, treatment planning, and patient monitoring. Reduces diagnostic errors and improves patient outcomes.',
      icon: Cpu,
      status: 'planned',
      priority: 'medium',
      category: 'ai'
    },
    {
      id: 'personalized-medicine',
      title: 'Personalized Medicine Platform',
      description: 'Tailored treatments based on genetic and health data',
      details: 'Platform that combines genetic information, health history, and lifestyle data to create personalized treatment plans and medication recommendations.',
      icon: Heart,
      status: 'in-development',
      priority: 'high',
      category: 'health'
    },
    {
      id: 'health-insights-dashboard',
      title: 'Health Insights Dashboard',
      description: 'Comprehensive health analytics and reporting',
      details: 'Advanced dashboard providing deep insights into your health patterns, trends, and correlations. Customizable reports and actionable health recommendations.',
      icon: Eye,
      status: 'released',
      priority: 'medium',
      category: 'analytics'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'released': return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      case 'beta': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400';
      case 'in-development': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'planned': return 'text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400';
      default: return 'text-slate-600 bg-slate-50 dark:bg-slate-700 dark:text-slate-400';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      case 'medium': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      default: return 'text-slate-600 bg-slate-50 dark:bg-slate-700 dark:text-slate-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className={`text-3xl font-bold ${theme.colors.text.primary} mb-4`}>Innovative Features</h2>
        <p className={`${theme.colors.text.secondary} max-w-2xl mx-auto`}>
          Discover cutting-edge health technology features that are transforming the way we approach healthcare and wellness.
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {innovativeFeatures.map((feature) => {
          const Icon = feature.icon;
          const isActive = activeFeature === feature.id;
          
          return (
            <div
              key={feature.id}
              className={`${theme.colors.background.card} rounded-xl border-2 p-6 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                isActive ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' : `${theme.colors.border.primary} hover:border-slate-300 dark:hover:border-slate-600`
              }`}
              onClick={() => setActiveFeature(isActive ? null : feature.id)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 ${
                  isActive ? 'scale-110' : 'scale-100'
                } transition-transform`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex flex-col items-end space-y-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(feature.status)}`}>
                    {feature.status.replace('-', ' ')}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(feature.priority)}`}>
                    {feature.priority}
                  </span>
                </div>
              </div>
              
              <h4 className={`text-lg font-semibold ${theme.colors.text.primary} mb-2`}>{feature.title}</h4>
              <p className={`text-sm ${theme.colors.text.secondary} mb-4`}>{feature.description}</p>
              
              {isActive && (
                <div className={`mt-4 p-4 ${theme.colors.background.primary} rounded-lg border ${theme.colors.border.accent} animate-in slide-in-from-top-2 duration-300`}>
                  <p className={`text-sm ${theme.colors.text.secondary} leading-relaxed`}>{feature.details}</p>
                </div>
              )}
              
              <div className="flex items-center justify-between mt-4">
                <span className={`text-xs ${theme.colors.text.muted} capitalize`}>
                  {feature.category}
                </span>
                <button
                  className={`text-xs font-medium ${
                    isActive 
                      ? 'text-blue-600 dark:text-blue-400' 
                      : `${theme.colors.text.tertiary} hover:text-blue-600 dark:hover:text-blue-400`
                  }`}
                >
                  {isActive ? 'Hide Details' : 'Learn More'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Categories */}
      <div className={`${theme.colors.background.card} rounded-xl border ${theme.colors.border.primary} p-6`}>
        <h3 className={`text-lg font-semibold ${theme.colors.text.primary} mb-4`}>Feature Categories</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { name: 'AI & Machine Learning', count: 2, color: 'blue' },
            { name: 'Security & Privacy', count: 2, color: 'green' },
            { name: 'Health & Wellness', count: 4, color: 'purple' },
            { name: 'Mobile & Accessibility', count: 1, color: 'orange' },
            { name: 'Analytics & Insights', count: 3, color: 'red' }
          ].map((category) => (
            <div key={category.name} className={`text-center p-4 rounded-lg border ${theme.colors.border.primary} ${theme.colors.interactive.hover}`}>
              <div className={`text-2xl font-bold ${theme.colors.text.primary}`}>{category.count}</div>
              <div className={`text-sm ${theme.colors.text.tertiary}`}>{category.name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Development Status */}
      <div className={`${theme.colors.background.card} rounded-xl border ${theme.colors.border.primary} p-6`}>
        <h3 className={`text-lg font-semibold ${theme.colors.text.primary} mb-4`}>Development Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { status: 'Released', count: 3, color: 'green' },
            { status: 'Beta', count: 3, color: 'blue' },
            { status: 'In Development', count: 3, color: 'yellow' },
            { status: 'Planned', count: 3, color: 'purple' }
          ].map((item) => (
            <div key={item.status} className="text-center">
              <div className={`text-2xl font-bold ${theme.colors.text.primary}`}>{item.count}</div>
              <div className={`text-sm ${theme.colors.text.tertiary}`}>{item.status}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
